import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Toolbar } from 'primeng/toolbar';
import { InputNumberModule } from 'primeng/inputnumber';

import { JalaliDatePipe } from '@/pipes/jalali-date.pipe';
import { AthleteClassesService } from '@/services/athlete-classes.service';

interface ExpiringRowDto {
  athleteId: number;
  athleteClassId: number;
  fullName: string;

  classId: number;
  classTitle: string;

  expiresAt: string | Date;
  signupPDate?: string | Date;
  registerDate?: string | Date;

  sessionPack?: string;
  totalSessions?: number;
  usedCount?: number;
  remainingSessions: number;
}

@Component({
  selector: 'app-expiring-enrollments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    ToastModule,
    Toolbar,
    InputNumberModule,
    JalaliDatePipe,
  ],
  providers: [MessageService],
  encapsulation: ViewEncapsulation.None,
  template: `
<p-toolbar class="mb-3">
  <ng-template #start>
    <div class="flex flex-col">
      <div class="font-bold text-lg">انقضاهای نزدیک</div>
      <div class="text-xs text-gray-500">ثبت‌نام‌هایی که تا چند روز آینده منقضی می‌شوند</div>
    </div>
  </ng-template>

  <ng-template #end>
    <div class="flex items-center gap-2">
      <div class="text-sm text-gray-600">تا</div>

      <p-inputNumber
        [(ngModel)]="days"
        [min]="1"
        [max]="30"
        [useGrouping]="false"
        inputStyleClass="days-input"
        (onInput)="onDaysChange()"
      ></p-inputNumber>

      <div class="text-sm text-gray-600">روز آینده</div>

      <p-button
        icon="pi pi-refresh"
        label="رفرش"
        severity="secondary"
        (onClick)="load()"
        [loading]="loading"
      ></p-button>
    </div>
  </ng-template>
</p-toolbar>

<p-table
  [value]="loading ? [] : rows"
  [loading]="loading"
  [paginator]="true"
  [rows]="pageSize"
  [rowsPerPageOptions]="[10,20,50]"
  responsiveLayout="scroll"
  stripedRows
>
  <ng-template pTemplate="header">
    <tr>
      <th style="width:70px">ردیف</th>
      <th>نام و نام خانوادگی</th>
      <th>نام کلاس</th>
      <th style="width:170px">تاریخ ثبت نام</th>
      <th style="width:180px">تاریخ انقضا</th>
      <th style="width:170px">جلسات باقی‌مانده</th>
      <th style="width:140px">وضعیت</th>
    </tr>
  </ng-template>

  <ng-template pTemplate="body" let-row let-i="rowIndex">
    <tr>
      <td>{{ i + 1 }}</td>

      <td class="font-bold">{{ row.fullName || ('ورزشکار ' + row.athleteId) }}</td>

      <td>{{ row.classTitle || '-' }}</td>

      <td>
        {{
          row.signupPDate
            ? row.signupPDate
            : row.registerDate
            ? (row.registerDate | jalaliDate:'YYYY/MM/DD')
            : '----/--/--'
        }}
      </td>

      <td>
        {{ row.expiresAt ? (row.expiresAt | jalaliDate:'YYYY/MM/DD') : '----/--/--' }}
      </td>

      <td>
        <span class="count-badge">
          {{ row.remainingSessions }} باقی‌مانده
        </span>
        <span class="text-xs text-gray-500 mr-2">
          ({{ (row.usedCount || 0) }} از {{ (row.totalSessions || 0) }})
        </span>
      </td>

      <td>
        <span
          class="status-pill"
          [class.status-warn]="isWarn(row)"
          [class.status-danger]="isDanger(row)"
          [class.status-ok]="isOk(row)"
        >
          {{ statusLabel(row) }}
        </span>
      </td>
    </tr>
  </ng-template>

  <ng-template pTemplate="emptymessage">
    <tr>
      <td colspan="6">
        <div class="text-sm text-gray-500 py-6 text-center">
          موردی برای نمایش وجود ندارد.
        </div>
      </td>
    </tr>
  </ng-template>
</p-table>

<p-toast position="top-left"></p-toast>
  `,
  styles: [`
.days-input{
  width:72px;
  text-align:center;
}

/* badges */
.count-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:4px 10px;
  border-radius:9999px;
  background:#f1f3f5;
  font-weight:900;
  font-size:12px;
  color:#343a40;
}

.status-pill{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:4px 10px;
  border-radius:9999px;
  font-size:12px;
  font-weight:900;
  border:1px solid transparent;
  white-space:nowrap;
}

.status-ok{ background:#dcfce7; color:#15803d; border-color:#bbf7d0; }
.status-warn{ background:#fff7ed; color:#9a3412; border-color:#fed7aa; }
.status-danger{ background:#fee2e2; color:#b91c1c; border-color:#fecaca; }
  `],
})
export class ExpiringEnrollmentsPage implements OnInit {
  rows: ExpiringRowDto[] = [];
  loading = false;

  days = 3;
  pageSize = 20;

  constructor(
    private athleteClassesService: AthleteClassesService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.load();
  }

  onDaysChange() {
    const d = Number(this.days || 3);
    this.days = Math.min(Math.max(d, 1), 30);
  }

  load() {
    this.loading = true;
    const d = Math.min(Math.max(Number(this.days || 3), 1), 30);

    this.athleteClassesService.getExpiringEnrollments(d).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        this.rows = (list || []).map((x: any) => ({
          athleteId: x.athleteId,
          athleteClassId: x.athleteClassId,
          fullName: x.fullName,
          classId: x.classId,
          classTitle: x.classTitle,
          expiresAt: x.expiresAt,
          signupPDate:
            x.signupPDate ||
            x.registerDate ||
            x.signupDate ||
            x.register_date ||
            x.signup_date ||
            x.createdAt ||
            x.created_at ||
            '',
          registerDate:
            x.registerDate ||
            x.signupPDate ||
            x.signupDate ||
            x.register_date ||
            x.signup_date ||
            x.createdAt ||
            x.created_at ||
            '',

          sessionPack: x.sessionPack,
          totalSessions: Number(x.totalSessions || 0),
          usedCount: Number(x.usedCount || 0),
          remainingSessions: Number(x.remainingSessions ?? 0),
        }));
        this.loading = false;
      },
      error: (e: any) => {
        this.rows = [];
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'خطا',
          detail: e?.error?.message || 'خطا در دریافت لیست انقضاهای نزدیک',
        });
      },
    });
  }

  // ---------- status UI ----------
  private expiresMs(row: ExpiringRowDto): number {
    try {
      return new Date(row.expiresAt as any).getTime();
    } catch {
      return 0;
    }
  }

  isDanger(row: ExpiringRowDto): boolean {
    const now = Date.now();
    const exp = this.expiresMs(row);
    return exp > 0 && exp <= now; // منقضی شده
  }

  isWarn(row: ExpiringRowDto): boolean {
    const now = Date.now();
    const exp = this.expiresMs(row);
    if (!exp) return false;
    return exp > now && row.remainingSessions <= 1; // نزدیک به اتمام جلسات
  }

  isOk(row: ExpiringRowDto): boolean {
    return !this.isDanger(row) && !this.isWarn(row);
  }

  statusLabel(row: ExpiringRowDto): string {
    if (this.isDanger(row)) return 'منقضی شده';
    if (this.isWarn(row)) return 'نیاز به تمدید';
    return 'نزدیک انقضا';
  }
}
